import { useTranslateMessage } from "@/hooks/useTranslateMessage";
import clsx from "clsx";
import { Children, FC, useEffect, useState } from "react";
import {
  Control,
  Controller,
  FieldValues,
  get,
  Path,
  useFormState,
  useWatch,
} from "react-hook-form";
import Select, { Props, components } from "react-select";
import colors from "tailwindcss/colors";

interface StyledSelectProps<T extends FieldValues> extends Props {
  title?: string;
  control: Control<T>;
  name: Path<T>;
  className?: React.HTMLAttributes<HTMLDivElement>["className"];
  setValue: (e: any) => void;
  options: { value: number; label: string }[];
}

const { ValueContainer, Placeholder } = components;

const CustomValueContainer: FC<any> = ({ children, ...props }) => {
  return (
    <ValueContainer {...props}>
      <Placeholder {...props} isFocused={props.isFocused}>
        {props.selectProps.placeholder}
      </Placeholder>
      {Children.map(children, (child) => (child && child.type !== Placeholder ? child : null))}
    </ValueContainer>
  );
};

export const StyledSelectSingleValueInt: FC<StyledSelectProps<any>> = ({
  title,
  control,
  name,
  className,
  setValue,
  options,
  ...props
}) => {
  const [selected, setSelected] = useState<{
    value: number;
    label: string;
  } | null>(null);
  const { errors } = useFormState({ control });
  const translateMessage = useTranslateMessage();
  const errorMessage = get(errors, name + ".message") as unknown as string;

  const watch = useWatch({
    control,
    name,
  });

  useEffect(() => {
    const matchingOption: { value: number; label: string } | null =
      options.find((el: { value: number; label: string }) => el.value === Number(watch)) || null;
    if (matchingOption) {
      setSelected(matchingOption);
      setValue(matchingOption?.value);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const handleChange = (e: any) => {
    const matchingOption: { value: number; label: string } | null =
      options.find((el: { value: number; label: string }) => el.value === e) || null;
    if (matchingOption) {
      setSelected(matchingOption);
      setValue(matchingOption?.value);
    }
  };

  return (
    <div className={clsx("flex flex-col gap-2", className)}>
      <Controller
        control={control}
        name={name}
        render={({ field }) => (
          <Select
            {...field}
            {...props}
            placeholder={title}
            options={options}
            onChange={(e: any) => handleChange(e.value)}
            value={selected}
            components={{
              IndicatorSeparator: () => null,
              ValueContainer: CustomValueContainer,
            }}
            className={clsx([className])}
            styles={{
              control: (baseStyles) => ({
                ...baseStyles,
                borderColor: colors.gray[300],
                height: "3.5rem",
                borderRadius: "0.5rem",
              }),
              placeholder: (baseStyles) => ({
                ...baseStyles,
                transform: watch ? "translateY(-1rem)" : "translateY(0)",
                fontWeight: 400,
                fontSize: watch ? "0.75rem" : "0.875rem",
              }),
              valueContainer: (baseStyles) => ({
                ...baseStyles,
                overflow: "visible",
                marginLeft: "0.4rem",
              }),
              singleValue: (baseStyles, state) => ({
                ...baseStyles,
                transform: state.hasValue ? "translateY(0.3rem)" : "translateY(0)",
              }),
              dropdownIndicator: (baseStyles) => ({
                ...baseStyles,
                padding: "2px",
              }),
            }}
          />
        )}
      />
      {errorMessage !== "" && (
        <p className="text-xs text-red-500">{translateMessage(errorMessage)}</p>
      )}
    </div>
  );
};
